import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import type { TrainingAnswer, TrainingAnswers, TrainingModuleSchema } from '@hcc/types';
import { Button } from '../../ui/Button';
import { Pill } from '../../ui/Pill';
import { ContentSlide } from '../player/ContentSlide';
import { QuestionSlide } from '../player/QuestionSlide';
import { QuestionGroupSlide } from '../player/QuestionGroupSlide';
import { TextGroupSlide } from '../player/TextGroupSlide';
import { FollowUpSlide } from '../player/FollowUpSlide';
import { pageComplete } from '../player/answerValues';
import { gradePreview, previewServe, toPreviewPages } from './previewView';

/**
 * Try-it-out preview hosting the real player components over a locally
 * redacted copy of the working schema, with local scoring so the admin
 * can verify the answer key end to end without publishing. When the module
 * draws a question bank, each Reset redraws a fresh subset just as a new
 * attempt would on the server.
 */
export function ModulePreview({
  schema,
  graded = true,
  passMark,
  serveQuestionCount,
  shuffleQuestions,
  focusPageId,
}: {
  schema: TrainingModuleSchema;
  /** False = an unmarked assessment: nothing to score, so nothing to preview. */
  graded?: boolean;
  passMark: number;
  serveQuestionCount: number | null;
  shuffleQuestions: boolean;
  /** When the builder selects/edits a page, jump the preview to it. */
  focusPageId?: string;
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<TrainingAnswers>({});
  const [result, setResult] = useState<ReturnType<typeof gradePreview> | null>(null);
  // Bumped on Reset so the seeded draw produces a different subset/order.
  const [drawSeed, setDrawSeed] = useState(1);

  const serve = useMemo(
    () => previewServe({ serveQuestionCount, shuffleQuestions }, drawSeed),
    [serveQuestionCount, shuffleQuestions, drawSeed],
  );
  const pages = useMemo(() => toPreviewPages(schema, serve), [schema, serve]);

  // Follow the editor: when the selected page changes, jump the preview to it
  // (by id, so a bank-dropped page just leaves the preview where it was).
  useEffect(() => {
    if (!focusPageId) return;
    const idx = pages.findIndex((p) => p.id === focusPageId);
    if (idx >= 0) {
      setStep(idx);
      setResult(null);
    }
    // Only react to the focused page changing, not every keystroke re-deriving pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPageId]);

  const safeStep = Math.min(step, Math.max(0, pages.length - 1));
  const page = pages[safeStep];
  const allDone = pages.every((p) => pageComplete(p, answers, pages));

  function reset() {
    setStep(0);
    setAnswers({});
    setResult(null);
    setDrawSeed((s) => s + 1);
  }

  return (
    <div className="rounded-token border border-line/10 bg-surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <Pill tone="accent" size="sm">
          {graded ? 'Preview — answers are graded locally' : 'Preview — nothing is marked'}
        </Pill>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
          onClick={reset}
        >
          Reset
        </Button>
      </div>
      {result && !graded ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Pill tone="success">Answers recorded</Pill>
          <p className="max-w-xs text-sm text-fg-muted">
            This is what the learner sees: no score, no pass mark. Their answers go to the addresses
            set below the module settings.
          </p>
        </div>
      ) : result ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span className="text-3xl font-bold tabular-nums text-fg">
            {Math.round(result.score)}%
          </span>
          <Pill tone={result.passed ? 'success' : 'warning'}>
            {result.passed ? 'Would pass' : `Would fail (pass mark ${passMark}%)`}
          </Pill>
          {result.sections.length > 0 ? (
            <ul className="mt-1 w-full space-y-1 text-left text-sm">
              {result.sections.map((s) => (
                <li
                  key={s.section_id}
                  className="flex items-center justify-between gap-2 rounded-token-sm bg-surface-2 px-2.5 py-1.5"
                >
                  <span className="truncate text-fg-muted">{s.title}</span>
                  {s.not_applicable ? (
                    <Pill tone="neutral" size="sm">
                      Not asked
                    </Pill>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="tabular-nums text-fg-dim">{Math.round(s.score)}%</span>
                      <Pill tone={s.passed ? 'success' : 'warning'} size="sm">
                        {s.passed ? 'Pass' : 'Fail'}
                      </Pill>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : !page ? (
        <p className="text-sm text-fg-muted">Add a page to see the preview.</p>
      ) : (
        <>
          <div className="min-h-48">
            {page.type === 'slide' ? (
              <ContentSlide slide={page} />
            ) : page.type === 'question_group' ? (
              <QuestionGroupSlide
                group={page}
                answers={answers}
                onChange={(rowId, optionId) => setAnswers((a) => ({ ...a, [rowId]: optionId }))}
              />
            ) : page.type === 'text_group' ? (
              <TextGroupSlide
                group={page}
                answers={answers}
                onChange={(rowId, text) => setAnswers((a) => ({ ...a, [rowId]: text }))}
              />
            ) : page.type === 'follow_up' ? (
              <FollowUpSlide
                page={page}
                pages={pages}
                answers={answers}
                onChange={(next: TrainingAnswer) => setAnswers((a) => ({ ...a, [page.id]: next }))}
              />
            ) : (
              <QuestionSlide
                question={page}
                answer={answers[page.id]}
                onChange={(next: TrainingAnswer) => setAnswers((a) => ({ ...a, [page.id]: next }))}
              />
            )}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-line/10 pt-3">
            <Button
              variant="ghost"
              size="sm"
              disabled={safeStep === 0}
              iconLeft={<ChevronLeft className="h-4 w-4" />}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-fg-dim">
              {safeStep + 1} / {pages.length}
            </span>
            {safeStep < pages.length - 1 ? (
              <Button
                variant="secondary"
                size="sm"
                iconRight={<ChevronRight className="h-4 w-4" />}
                onClick={() => setStep((s) => Math.min(pages.length - 1, s + 1))}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="success"
                size="sm"
                disabled={!allDone}
                onClick={() => setResult(gradePreview(schema, answers, passMark, serve))}
              >
                Finish
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
